import { iconMarcaLogoSaude, iconMarcaLogoPrevidencia, iconMarcaLogoVida, iconDocAnalise, iconSetaDireita } from '@bsds-lib-component/icons/icons';
import { BsdsCard, BsdsInput, BsdsIcon } from '@bsds-lib-component/react';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

import { Button } from '../../components';

interface Report {
  id: string;
  name: string;
  category: 'saude' | 'previdencia' | 'vida' | 'geral';
}

interface ReportCategory {
  id: 'saude' | 'previdencia' | 'vida' | 'geral';
  name: string;
  icon: string;
  reports: Report[];
}

const ReportsPage: React.FC = () => {
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);

  const mainContentId = useId();
  const categoriesId = useId();
  const filtersId = useId();

  const categoryButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const arrowIconRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const dropdownRef = useRef<HTMLDivElement>(null);

  const categories: ReportCategory[] = [
    {
      id: 'saude',
      name: 'Saúde',
      icon: iconMarcaLogoSaude,
      reports: [
        { id: 'saude-1', name: 'Relatório de Pagamentos', category: 'saude' },
        { id: 'saude-2', name: 'Relatório de Beneficiários', category: 'saude' },
        { id: 'saude-3', name: 'Relatório de Consultas', category: 'saude' },
        { id: 'saude-4', name: 'Relatório de Procedimentos', category: 'saude' },
        { id: 'saude-5', name: 'Relatório de Faturamento', category: 'saude' },
        { id: 'saude-6', name: 'Relatório de Adesões', category: 'saude' },
      ],
    },
    {
      id: 'previdencia',
      name: 'Previdência',
      icon: iconMarcaLogoPrevidencia,
      reports: [
        { id: 'previdencia-1', name: 'Relatório de Pesquisa de Participantes', category: 'previdencia' },
        { id: 'previdencia-2', name: 'Relatório de Download de Adesões', category: 'previdencia' },
        { id: 'previdencia-3', name: 'Relatório de Faturamento Digital', category: 'previdencia' },
        { id: 'previdencia-4', name: 'Relatório de Pagamentos', category: 'previdencia' },
        { id: 'previdencia-5', name: 'Relatório de Participantes Ativos', category: 'previdencia' },
        { id: 'previdencia-6', name: 'Relatório de Movimentações', category: 'previdencia' },
      ],
    },
    {
      id: 'vida',
      name: 'Vida',
      icon: iconMarcaLogoVida,
      reports: [
        { id: 'vida-1', name: 'Relatório de Pagamentos', category: 'vida' },
        { id: 'vida-2', name: 'Relatório de Dashboard Pagamentos', category: 'vida' },
        { id: 'vida-3', name: 'Relatório de Consulta de Apólices', category: 'vida' },
        { id: 'vida-4', name: 'Relatório de Proposta de Adesão', category: 'vida' },
        { id: 'vida-5', name: 'Relatório de Beneficiários', category: 'vida' },
        { id: 'vida-6', name: 'Relatório de Faturamento', category: 'vida' },
      ],
    },
    {
      id: 'geral',
      name: 'Geral',
      icon: iconDocAnalise,
      reports: [
        { id: 'geral-1', name: 'Relatório de Pagamentos', category: 'geral' },
        { id: 'geral-2', name: 'Relatório de Dashboard Pagamentos', category: 'geral' },
        { id: 'geral-3', name: 'Relatório de Consulta de Apólices', category: 'geral' },
        { id: 'geral-4', name: 'Relatório de Proposta de Adesão', category: 'geral' },
        { id: 'geral-5', name: 'Relatório de Beneficiários', category: 'geral' },
        { id: 'geral-6', name: 'Relatório de Faturamento', category: 'geral' },
      ],
    },
  ];

  // Calculates the dropdown position based on screen size
  const calculateDropdownPosition = useCallback(() => {
    if (openCategory) {
      const button = categoryButtonRefs.current.get(openCategory);
      const arrowIcon = arrowIconRefs.current.get(openCategory);
      if (button) {
        const rect = button.getBoundingClientRect();
        let leftPosition = rect.left + window.scrollX;

        // On md screens and larger, position where the arrow icon ends
        if (window.innerWidth >= 768 && arrowIcon) {
          const arrowRect = arrowIcon.getBoundingClientRect();
          leftPosition = arrowRect.right + window.scrollX + 20; // 20px spacing after the icon

          setDropdownPosition({
            top: rect.bottom + window.scrollY - 20, // 20px offset from button bottom
            left: leftPosition,
          });
        } else {
          // On mobile, position at button start with offset
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: leftPosition - 21, // 21px offset to the left
          });
        }
      }
    } else {
      setDropdownPosition(null);
    }
  }, [openCategory]);

  // Calculates dropdown position when a category is opened
  useEffect(() => {
    calculateDropdownPosition();
  }, [calculateDropdownPosition]);

  // Recalculates position when window is resized
  useEffect(() => {
    if (openCategory) {
      const handleResize = () => {
        calculateDropdownPosition();
      };
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
    return undefined;
  }, [openCategory, calculateDropdownPosition]);

  // Closes dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openCategory && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        const button = categoryButtonRefs.current.get(openCategory);
        if (button && !button.contains(event.target as Node)) {
          setOpenCategory(null);
        }
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && openCategory) {
        setOpenCategory(null);
      }
    };

    if (openCategory) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openCategory]);

  const handleCategoryClick = (categoryId: string) => {
    // If clicking the same category, toggle dropdown open/close
    if (openCategory === categoryId) {
      setOpenCategory(null);
    } else {
      // Selects the new category and removes previous selection
      setSelectedCategory(categoryId);
      setOpenCategory(categoryId);
      // Clears selected report if it belongs to a different category
      if (selectedReport && selectedReport.category !== categoryId) {
        setSelectedReport(null);
        setStartDate('');
        setEndDate('');
      }
    }
  };

  const handleReportClick = (report: Report) => {
    setSelectedReport(report);
    setSelectedCategory(report.category);
    setStartDate('');
    setEndDate('');
    setOpenCategory(null);
  };

  const handleGenerateReport = () => {
    if (!selectedReport || !startDate || !endDate) return;
    console.log('Gerar relatório:', { report: selectedReport.name, startDate, endDate });
  };

  const isGenerateButtonEnabled = selectedReport && startDate && endDate && new Date(startDate) <= new Date(endDate);

  const currentCategory = categories.find(c => c.id === openCategory);

  return (
    <div className="bsds-width-100" role="main" aria-label="Página de Relatórios" id={mainContentId}>
      <div className="bsds-display-flex bsds-align-items-center">
        <div className="w-[2.5px] h-8 bg-red-600 bsds-mr-12" aria-hidden="true" />
        <h2 className="opacity-80">Relatórios</h2>
      </div>

      <div className="bsds-display-flex flex-col lg:flex-row bsds-mt-24 gap-8">
        {/* Categories Card */}
        <BsdsCard className="w-full lg:w-64 p-6 flex-shrink-0" platform="web" role="region" aria-label="Categorias de relatórios" id={categoriesId}>
          <h3 className="text-lg font-semibold text-gray-900 bsds-mb-16">Categorias</h3>
          <div className="flex flex-col gap-4">
            {categories.map((category) => {
              const isSelected = selectedCategory === category.id;
              return (
                <button
                  key={category.id}
                  ref={(el) => {
                    if (el) {
                      categoryButtonRefs.current.set(category.id, el);
                    } else {
                      categoryButtonRefs.current.delete(category.id);
                    }
                  }}
                  className={`group w-full bsds-display-flex bsds-align-items-center bsds-justify-content-start gap-3 rounded-md transition-all duration-200 text-left relative overflow-hidden ${isSelected
                    ? 'px-3 py-3 shadow-custom-border'
                    : 'p-3 hover:px-3 hover:py-3'
                    }`}
                  onClick={() => handleCategoryClick(category.id)}
                  aria-expanded={openCategory === category.id}
                  aria-label={`Abrir relatórios de ${category.name}`}
                >
                  {/* Sidebar */}
                  <div
                    className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-md transition-colors duration-200 ${isSelected
                      ? 'bg-primary-500'
                      : 'bg-transparent group-hover:bg-primary-500'
                      }`}
                    aria-hidden="true"
                  />
                  {/* Effect of filling from left to right */}
                  <div
                    className={`absolute inset-0 left-1 bg-gray-50 transition-transform duration-300 ease-out origin-left ${isSelected
                      ? 'scale-x-100'
                      : 'scale-x-0 group-hover:scale-x-100'
                      }`}
                    aria-hidden="true"
                  />
                  <BsdsIcon icon={category.icon} className="relative z-10 w-5 h-5 transition-colors duration-200" aria-hidden="true" />
                  <span className="relative z-10 text-sm font-medium text-gray-700 transition-colors duration-200 flex-1">{category.name}</span>
                  <div
                    ref={(el) => {
                      if (el) {
                        arrowIconRefs.current.set(category.id, el);
                      } else {
                        arrowIconRefs.current.delete(category.id);
                      }
                    }}
                  >
                    <BsdsIcon
                      icon={iconSetaDireita}
                      className=" w-4 h-4 transition-colors duration-200"
                      aria-hidden="true"
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </BsdsCard>

        {/* Dropdown outside card with fixed position */}
        {openCategory && currentCategory && dropdownPosition && (
          <div
            ref={dropdownRef}
            className="fixed w-80 bg-white rounded-lg shadow-2xl border border-gray-200 p-5 z-50"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
            role="menu"
            aria-label={`Relatórios da categoria ${currentCategory.name}`}
          >
            <h4 className="text-sm font-semibold text-gray-900 bsds-mb-12">
              Relatórios de {currentCategory.name}
            </h4>
            <div className="flex flex-col gap-1 max-h-96 overflow-y-auto">
              {currentCategory.reports.map((report) => (
                <button
                  key={report.id}
                  className={`text-left px-4 py-2.5 rounded-md text-sm transition-colors ${selectedReport?.id === report.id
                    ? 'bg-red-50 text-red-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100/40'
                    }`}
                  onClick={() => handleReportClick(report)}
                  role="menuitem"
                >
                  {report.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Filters Card */}
        <div className="flex-1">
          {selectedReport ? (
            <BsdsCard className="p-6" platform="web" role="region" aria-label="Filtros do relatório" id={filtersId}>
              <h3 className="text-lg font-semibold text-gray-900 bsds-mb-16">{selectedReport.name}</h3>
              <div className="flex flex-col gap-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <BsdsInput
                    type="date"
                    label="Data Inicial"
                    value={startDate}
                    onInput={(e: React.FormEvent<HTMLBsdsInputElement> & { detail?: { value: string } }) => {
                      const target = e.target as HTMLInputElement;
                      setStartDate(e.detail?.value || target?.value || '');
                    }}
                    platform="web"
                  />
                  <BsdsInput
                    type="date"
                    label="Data Final"
                    value={endDate}
                    onInput={(e: React.FormEvent<HTMLBsdsInputElement> & { detail?: { value: string } }) => {
                      const target = e.target as HTMLInputElement;
                      setEndDate(e.detail?.value || target?.value || '');
                    }}
                    platform="web"
                  />
                </div>
                <Button
                  color="primary"
                  fill="solid"
                  onClick={handleGenerateReport}
                  disabled={!isGenerateButtonEnabled}
                >
                  Gerar Relatório
                </Button>
              </div>
            </BsdsCard>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;